import React, {
  CSSProperties,
  FC,
  forwardRef,
  HTMLAttributes
} from 'react'

// Mapping for numeric values corresponding to known Tailwind classes
const spacingMap: Record<number, string> = {
  0: '0',
  1: '1',
  2: '2',
  3: '3',
  4: '4',
  5: '5',
  6: '6',
  8: '8',
  10: '10',
  12: '12',
  14: '14',
  16: '16',
  20: '20',
  24: '24',
  32: '32',
  40: '40',
  48: '48',
  56: '56',
  64: '64'
}

type SpacingValue = number | string | undefined

export interface BoxProps extends HTMLAttributes<HTMLDivElement> {
  // Margin
  m?: SpacingValue
  mt?: SpacingValue
  mr?: SpacingValue
  mb?: SpacingValue
  ml?: SpacingValue
  mx?: SpacingValue
  my?: SpacingValue
  // Padding
  p?: SpacingValue
  pt?: SpacingValue
  pr?: SpacingValue
  pb?: SpacingValue
  pl?: SpacingValue
  px?: SpacingValue
  py?: SpacingValue
  // Flexbox
  flex?: boolean
  column?: boolean
  justify?: 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly'
  align?: 'start' | 'center' | 'end' | 'stretch' | 'baseline'
  grow?: number
  // Float
  float?: 'left' | 'right' | 'none'
  gap?:
    | number
    | '0'
    | '1'
    | '2'
    | '3'
    | '4'
    | '5'
    | '6'
    | '8'
    | '10'
    | '12'
    | '16'
    | '20'
    | '24'
    | '32'
    | '40'
    | '48'
  // Width/height
  w?: number | string
  h?: number | string
  width?: string
  height?: string
  // Additional classes
  className?: string
}

interface SpacingDefinition {
  propName: keyof BoxProps
  cssProp: keyof CSSProperties
  classPrefix: string
  composite?: boolean // For mx/my or px/py
  cssProp2?: keyof CSSProperties // The second CSS property for composite props
}

const spacingDefinitions: SpacingDefinition[] = [
  // Margin
  {propName: 'm', cssProp: 'margin', classPrefix: 'm'},
  {propName: 'mt', cssProp: 'marginTop', classPrefix: 'mt'},
  {propName: 'mr', cssProp: 'marginRight', classPrefix: 'mr'},
  {propName: 'mb', cssProp: 'marginBottom', classPrefix: 'mb'},
  {propName: 'ml', cssProp: 'marginLeft', classPrefix: 'ml'},
  {
    propName: 'mx',
    cssProp: 'marginLeft',
    classPrefix: 'mx',
    composite: true,
    cssProp2: 'marginRight'
  },
  {
    propName: 'my',
    cssProp: 'marginTop',
    classPrefix: 'my',
    composite: true,
    cssProp2: 'marginBottom'
  },
  // Padding
  {propName: 'p', cssProp: 'padding', classPrefix: 'p'},
  {propName: 'pt', cssProp: 'paddingTop', classPrefix: 'pt'},
  {propName: 'pr', cssProp: 'paddingRight', classPrefix: 'pr'},
  {propName: 'pb', cssProp: 'paddingBottom', classPrefix: 'pb'},
  {propName: 'pl', cssProp: 'paddingLeft', classPrefix: 'pl'},
  {
    propName: 'px',
    cssProp: 'paddingLeft',
    classPrefix: 'px',
    composite: true,
    cssProp2: 'paddingRight'
  },
  {
    propName: 'py',
    cssProp: 'paddingTop',
    classPrefix: 'py',
    composite: true,
    cssProp2: 'paddingBottom'
  }
]

/**
 * Processes a single spacing prop and returns either a Tailwind class or inline style.
 */
const processValue = (
  value: SpacingValue,
  cssProp: keyof CSSProperties,
  classPrefix: string
): {className?: string; style?: CSSProperties} => {
  if (typeof value === 'number') {
    if (spacingMap[value] !== undefined) {
      return {className: `${classPrefix}-${spacingMap[value]}`}
    }
    return {style: {[cssProp]: `${value}px`}}
  }

  if (typeof value === 'string') {
    // If the string starts with a digit, assume it includes a unit
    if (/^\d/.test(value)) {
      return {style: {[cssProp]: value}}
    }
    // Otherwise, assume it's a Tailwind class suffix (e.g., "auto", "px", etc.)
    return {className: `${classPrefix}-${value}`}
  }

  return {}
}

/**
 * Highly-optimized Box component for margin/padding shorthand, flex, float, etc.
 */
export const SharedBox: FC<BoxProps> = forwardRef<HTMLDivElement, BoxProps>(
  (props, ref) => {
    const {
      m,
      mt,
      mr,
      mb,
      ml,
      mx,
      my,
      p,
      pt,
      pr,
      pb,
      pl,
      px,
      py,
      flex,
      column,
      justify,
      align,
      grow,
      float,
      gap,
      w,
      h,
      width,
      height,
      className,
      style: styleProp,
      children,
      ...rest
    } = props

    // Gather all spacing props into a single object
    const spacingProps: Record<string, SpacingValue> = {
      m,
      mt,
      mr,
      mb,
      ml,
      mx,
      my,
      p,
      pt,
      pr,
      pb,
      pl,
      px,
      py
    }

    const computedClassNames: string[] = []
    const computedStyles: CSSProperties = {}

    // Process only defined props to avoid unnecessary operations
    spacingDefinitions.forEach(def => {
      const value = spacingProps[def.propName]
      if (value === undefined) {
        return
      }

      const result = processValue(value, def.cssProp, def.classPrefix)

      if (result.className) {
        computedClassNames.push(result.className)
      }

      if (result.style) {
        Object.assign(computedStyles, result.style)
      }

      // For composite props (e.g., mx, my, px, py), apply the same value to the second CSS property if no class was found
      if (def.composite && def.cssProp2 && !result.className) {
        ;(computedStyles as any)[def.cssProp2] =
          typeof value === 'number' ? `${value}px` : value
      }
    })

    // Handle gap
    if (gap) {
      if (typeof gap === 'number') {
        computedStyles.gap = `${gap}px`
      } else {
        computedClassNames.push(`gap-${gap}`)
      }
    }

    // Width
    if (w) {
      computedStyles.width = typeof w === 'number' ? `${w}px` : w
    } else if (width) {
      computedStyles.width = width
    }

    // Height
    if (h) {
      computedStyles.height = typeof h === 'number' ? `${h}px` : h
    } else if (height) {
      computedStyles.height = height
    }

    if (flex) {
      computedClassNames.push('flex')
    }

    if (column) {
      computedClassNames.push('flex-col')
    }

    if (justify) {
      computedClassNames.push(`justify-${justify}`)
    }

    if (align) {
      computedClassNames.push(`items-${align}`)
    }

    if (grow !== undefined) {
      computedStyles.flexGrow = grow
    }

    if (float) {
      computedClassNames.push(`float-${float}`)
    }

    // Merge classes and inline styles
    const finalClassName = [...computedClassNames, className]
      .filter(Boolean)
      .join(' ')
    const finalStyle = {...computedStyles, ...styleProp}

    return (
      <div ref={ref} className={finalClassName} style={finalStyle} {...rest}>
        {children}
      </div>
    )
  }
)
